'use client';
import { useState } from 'react';
import { Button } from '@benzinga/core-ui';
import { SmartLink } from '@benzinga/analytics';
import styled from '@benzinga/themetron';
import classnames from 'classnames';
import { Product } from '@benzinga/content-manager';
import { sanitizeHTML } from '@benzinga/frontend-utils';

const FusionServiceCard = ({ data, details, image, link, name }: Product) => {
  const [truncated, setTruncated] = useState(true);

  const toggleTruncated = () => {
    setTruncated(!truncated);
  };

  return (
    <CardWrapper>
      <Header>
        <Logo alt={name} src={image} />
        <Avatar alt={data?.instructor_name?.display_value} src={data?.instructor_image?.value as string} />
      </Header>
      <Content>
        {details?.desc && (
          <Description
            className={classnames({
              truncated,
            })}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(details?.desc) }}
          />
        )}
        <ReadMoreButton onClick={toggleTruncated}>Read {truncated ? 'More' : 'Less'}</ReadMoreButton>
      </Content>
      <Footer>
        {data?.product_pricing_annual && (
          <PriceContainer>
            <Price>{data.product_pricing_annual.display_value}</Price>
            <PriceCaption>annually</PriceCaption>
          </PriceContainer>
        )}
        <StyledLink href={link}>
          <Button size="fusion-md" variant="flat-blue">
            GET STARTED
          </Button>
        </StyledLink>
      </Footer>
    </CardWrapper>
  );
};

export default FusionServiceCard;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colorPalette.white};
  border: 1px solid ${({ theme }) => theme.colorPalette.gray200};
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(25, 68, 128, 0.05);
  transition: 0.25s box-shadow ease-in-out;

  &:hover {
    box-shadow: 0 2px 16px rgba(25, 68, 128, 0.1);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
  padding: 0 16px 0 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colorPalette.gray100};
`;

const Logo = styled.img`
  display: block;
  height: 40px;
  max-width: 100%;
`;

const Avatar = styled.img`
  display: block;
  height: 48px;
  width: 48px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
  @media (max-width: 959px) {
    display: none;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colorPalette.gray100};
`;

const Description = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.colorPalette.gray500};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.normal};

  &.truncated {
    -webkit-line-clamp: 3;

    & div,
    & p {
      display: inline;
    }
  }
`;

const ReadMoreButton = styled.button`
  color: ${({ theme }) => theme.colorPalette.blue500};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: ${({ theme }) => theme.lineHeight[5]};
  text-transform: uppercase;
  margin: 8px 0 0;
  padding: 0;
  border: 0;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 80px;
  padding: 16px;

  @media (max-width: 959px) {
    flex-direction: column;
    align-items: flex-start;
    height: unset;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Price = styled.h5`
  color: ${({ theme }) => theme.colorPalette.gray700};
  font-size: ${({ theme }) => theme.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin-bottom: 0;
  white-space: nowrap;
  text-align: center;
`;

const PriceCaption = styled.span`
  font-size: ${({ theme }) => theme.fontSize['lg']};
  color: ${({ theme }) => theme.colorPalette.gray400};
`;

const StyledLink = styled(SmartLink)`
  margin-left: auto;
  & button {
    width: 100%;
  }

  @media (max-width: 959px) {
    width: 100%;
  }
`;
